import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonWithIcon, CreateIcon, actionIconStyles } from '../../../shared/components';
import { TaskCategoryItem } from '../types/Task';
import { buildCategoryListRows, getTopLevelCategories } from '../utils/taskCategoryUtils';
import styles from './TaskCategoryModal.module.css';

type TaskCategoryModalProps = {
  isOpen: boolean;
  onClose: () => void;
  categoryDraft: string;
  setCategoryDraft: (value: string) => void;
  parentCategoryId: string;
  setParentCategoryId: (value: string) => void;
  categoryBusy: boolean;
  categoryError: string | null;
  categoryOptions: TaskCategoryItem[];
  onCreateCategory: () => Promise<void>;
  onDeleteCategory: (categoryId: string) => Promise<void>;
  onAddSubcategory: (parentCategoryId: string) => void;
};

export const TaskCategoryModal: React.FC<TaskCategoryModalProps> = ({
  isOpen,
  onClose,
  categoryDraft,
  setCategoryDraft,
  parentCategoryId,
  setParentCategoryId,
  categoryBusy,
  categoryError,
  categoryOptions,
  onCreateCategory,
  onDeleteCategory,
  onAddSubcategory,
}) => {
  const { t } = useTranslation(['tasks', 'common']);
  const topLevelCategories = useMemo(() => getTopLevelCategories(categoryOptions), [categoryOptions]);
  const categoryRows = useMemo(() => buildCategoryListRows(categoryOptions), [categoryOptions]);
  const isSubcategoryMode = Boolean(parentCategoryId);
  const selectedParent = topLevelCategories.find(category => category.id === parentCategoryId);

  if (!isOpen) {
    return null;
  }

  return (
    <div className={styles.modalOverlay} role="dialog" aria-modal="true" aria-label={t('manageCategories', { ns: 'tasks' })} onClick={onClose}>
      <div className={styles.modalCard} onClick={event => event.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>{t('manageCategories', { ns: 'tasks' })}</h3>
          <button className={styles.modalCloseBtn} type="button" onClick={onClose} aria-label={t('close', { ns: 'common' })} disabled={categoryBusy}>×</button>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.createSection}>
            <label className={styles.fieldLabel} htmlFor="task-category-parent">
              {t('parentCategoryLabel', { ns: 'tasks' })}
            </label>
            <select
              id="task-category-parent"
              className={`text-input ${styles.parentSelect}`}
              value={parentCategoryId}
              onChange={(event) => setParentCategoryId(event.target.value)}
              disabled={categoryBusy}
            >
              <option value="">{t('topLevelCategoryOption', { ns: 'tasks' })}</option>
              {topLevelCategories.map(category => (
                <option key={category.id} value={category.id}>{category.name}</option>
              ))}
            </select>

            <label className={styles.fieldLabel} htmlFor="task-category-name">
              {isSubcategoryMode ? t('subcategoryNameLabel', { ns: 'tasks' }) : t('categoryNameLabel', { ns: 'tasks' })}
            </label>
            <div className={styles.createRow}>
              <input
                id="task-category-name"
                className={`text-input ${styles.categoryInput}`}
                value={categoryDraft}
                onChange={(event) => setCategoryDraft(event.target.value)}
                placeholder={
                  isSubcategoryMode
                    ? t('subcategoryNamePlaceholder', { ns: 'tasks', parent: selectedParent?.name ?? '' })
                    : t('categoryNamePlaceholder', { ns: 'tasks' })
                }
                maxLength={100}
              />
              <button className={`btn btn-primary ${actionIconStyles.buttonWithIcon}`} type="button" onClick={() => void onCreateCategory()} disabled={categoryBusy}>
                <ButtonWithIcon icon={<CreateIcon />}>
                  {isSubcategoryMode ? t('addSubcategory', { ns: 'tasks' }) : t('addCategory', { ns: 'tasks' })}
                </ButtonWithIcon>
              </button>
            </div>
          </div>

          {categoryError ? <div className="error">{categoryError}</div> : null}

          <div className={styles.categoryList}>
            {categoryRows.map(({ category, depth, canAddSubcategory }) => (
              <div
                key={category.id}
                className={`${styles.categoryItem} ${depth > 0 ? styles.categoryItemChild : ''}`}
                style={{ marginInlineStart: `${depth * 20}px` }}
              >
                <span className={styles.categoryName}>{category.name}</span>
                <div className={styles.categoryActions}>
                  {canAddSubcategory ? (
                    <button
                      className={`btn btn-secondary ${actionIconStyles.buttonWithIcon}`}
                      type="button"
                      onClick={() => onAddSubcategory(category.id)}
                      disabled={categoryBusy}
                    >
                      <ButtonWithIcon icon={<CreateIcon />}>{t('addSubcategory', { ns: 'tasks' })}</ButtonWithIcon>
                    </button>
                  ) : null}
                  <button className="btn btn-secondary" type="button" onClick={() => void onDeleteCategory(category.id)} disabled={categoryBusy}>
                    {t('delete', { ns: 'common' })}
                  </button>
                </div>
              </div>
            ))}
            {categoryRows.length === 0 ? <div className={styles.emptyState}>{t('noCategories', { ns: 'tasks' })}</div> : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskCategoryModal;
